import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Routes } from '@/routes/routeNames';
import { LuvelleWordmark } from '@/components/shared/LuvelleWordmark';
import { LanguageToggle } from '@/components/shared/LanguageToggle';
import { useAuth } from '@/contexts/AuthContext';

/**
 * SplashPage — first surface at `/`.
 *
 * Brand treatment: full-bleed plum gradient with the lotus mark + serif
 * wordmark centered. After auth resolves we always route to `/home`:
 *
 *   - The catalog itself is now address-optional (browse-everywhere
 *     posture, 2026-05-24). Customers can explore categories and
 *     service detail without committing to an address; the address
 *     ask only kicks in at the booking wizard.
 *   - /home renders the address chip when one is set, or a "Pin
 *     your address" CTA when one isn't.
 *
 * The splash never sends a guest to /login: per product, login is only
 * required when committing a booking.
 */
export function Splash() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { auth } = useAuth();

  useEffect(() => {
    if (auth.status === 'loading') return;
    const timer = window.setTimeout(() => {
      navigate(Routes.home.path, { replace: true });
    }, 1600);
    return () => window.clearTimeout(timer);
  }, [navigate, auth.status]);

  return (
    <main
      className="relative min-h-screen w-full bg-brand-gradient text-white flex flex-col items-center justify-center overflow-hidden"
      role="status"
    >
      {/* Soft pink halo top-left, deep plum vignette bottom-right. */}
      <div className="absolute -top-40 -start-40 w-[28rem] h-[28rem] rounded-full bg-pink-200/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -end-40 w-[32rem] h-[32rem] rounded-full bg-plum-900/40 blur-3xl pointer-events-none" />

      {/* Language toggle floats in the corner — guests can switch before
       * the splash redirects. */}
      <div className="absolute top-6 end-6 z-10 safe-top">
        <LanguageToggle variant="ghost" />
      </div>

      <div className="relative z-10 flex flex-col items-center gap-8 px-6 animate-fade-in">
        <LuvelleWordmark size="xl" showTagline />
        <p className="text-pink-100/90 text-center text-base md:text-lg max-w-sm leading-relaxed font-light">
          {t('splash.tagline')}
        </p>
      </div>

      <div className="absolute bottom-12 inset-x-0 flex items-center justify-center gap-2 text-pink-100/70 text-xs z-10 safe-bottom">
        <span className="w-1.5 h-1.5 rounded-full bg-pink-200 animate-pulse" />
        <span>{t('splash.loading')}</span>
      </div>
    </main>
  );
}

export default Splash;
